import dayjs, { Dayjs } from 'dayjs';
import { truthyNumber } from '@/common/utils/validation';
import { parsePeriod } from '@/common/utils/core/time/parse';
import { isNil, padEnd } from 'lodash-es';
import {
  GLOBAL_TIME_LIVE_FORMAT,
  UTC_TIME_FORMAT,
  GLOBAL_TIME_RANGE_FORMAT,
} from '@/common/components/molecules/timeController/constants';
import { TimeFormat } from '@/common/components/molecules/timeController/types';
import { ONE_DAY_IN_SECOND, ONE_HOUR_IN_SEC, ONE_MINUTE_IN_SEC } from './define';
import { TimePeriodType } from './types';
import { i18n } from '../locale';

export const convertSecondToTime = (_seconds: number): string => {
  let seconds = _seconds;
  if (seconds < 0 || !Number.isInteger(seconds)) {
    console.warn('Invalid uptime value');
  }

  const days = Math.floor(seconds / (60 * 60 * 24));
  seconds %= 24 * 60 * 60;

  const hours = Math.floor(seconds / (60 * 60));
  seconds %= 60 * 60;

  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  const result: string[] = [];
  if (days > 0) result.push(`${days}d`);
  if (hours > 0) result.push(`${hours}h`);
  if (minutes > 0) result.push(`${minutes}m`);
  if (seconds > 0 || !result.length) result.push(`${seconds}s`);

  return result.join(' ');
};

export const getSecondTimeRangeByTimePeriod = (timePeriod: TimePeriodType): number => {
  const time = +timePeriod.slice(1, -1);
  const unit = timePeriod.slice(-1);
  let range: number;
  switch (unit) {
    case 'm':
      range = time * 60;
      break;
    case 'h':
      range = time * 3600;
      break;
    case 'd':
      range = time * ONE_DAY_IN_SECOND;
      break;
    default:
      range = time;
  }
  return range;
};

export const getSecondByDays = (days: number): number => {
  return days * ONE_DAY_IN_SECOND;
};

export const getDayBySecond = (second: number): number => {
  return Math.floor(second / ONE_DAY_IN_SECOND);
};

export const convertNanoSecondToMilliSecond = (nanoSecond: string): number => {
  return Math.floor(Number(BigInt(nanoSecond) / BigInt(1_000_000)));
};

export const convertNanoToFomattedTime = (nanoSecond: string): string => {
  return dayjs(convertNanoSecondToMilliSecond(nanoSecond)).format('YYYY-MM-DD HH:mm:ss');
};

export const isDateValid = (date: string): boolean => {
  return dayjs(date).isValid();
};

/**
 *  seconds를 period로 변환
 * @param seconds seconds
 * @example secondsToPeriod(7200) => { unit: 'h', value: 2, locale: 'HOURS' }
 */
export const secondsToPeriod = (
  seconds: number,
): {
  unit: string;
  value: number;
  locale: string;
} => {
  if (Math.floor(seconds / 60) === 0) {
    return {
      unit: 's',
      value: seconds,
      locale: seconds === 1 ? 'SECOND' : 'SECONDS',
    };
  }
  if (Math.floor(seconds / 3600) === 0) {
    return {
      unit: 'm',
      value: Math.floor(seconds / 60),
      locale: Math.floor(seconds / 60) === 1 ? 'MINUTE' : 'MINUTES',
    };
  }
  if (Math.floor(seconds / ONE_DAY_IN_SECOND) === 0) {
    return {
      unit: 'h',
      value: Math.floor(seconds / 3600),
      locale: Math.floor(seconds / 3600) === 1 ? 'HOUR' : 'HOURS',
    };
  }
  return {
    unit: 'd',
    value: Math.floor(seconds / ONE_DAY_IN_SECOND),
    locale: Math.floor(seconds / ONE_DAY_IN_SECOND) === 1 ? 'DAY' : 'DAYS',
  };
};

export const getLocaleAndValueFrom = (
  code: `p${number}${string}` | `I${number}${string}`,
): { value: number; locale: string } => {
  const value = +code.slice(1, -1);
  const unit = code.slice(-1);
  let locale = '';
  switch (unit) {
    case 's':
      locale = value === 1 ? 'SECOND' : 'SECONDS';
      break;
    case 'm':
      locale = value === 1 ? 'MINUTE' : 'MINUTES';
      break;
    case 'h':
      locale = value === 1 ? 'HOUR' : 'HOURS';
      break;
    case 'd':
      locale = value === 1 ? 'DAY' : 'DAYS';
      break;
    default:
      locale = '';
  }
  return { value, locale };
};

export const getPeriodToSeconds = (period: string): number => {
  const unit = period?.slice(-1);
  const value = parseInt(period?.slice(0, -1), 10);

  if (truthyNumber(value)) {
    switch (unit) {
      case 's':
        return value;
      case 'm':
        return value * 60;
      case 'h':
        return value * 60 * 60;
      case 'd':
        return value * 60 * 60 * 24;
      case 'M':
      case 'month':
        return value * 60 * 60 * 24 * 31;
      default:
        return -1;
    }
  }

  return -1;
};

export const isDateTime = (value: any) =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(.\d+)?/.test(value) &&
  isDateValid(value);

/**
 * utc 타임을 dayjs 타입으로 변환
 * @param utc string | number | Date | dayjs.Dayjs
 * @returns Dayjs
 */
export const getTzTimeFromUtc = (utc: string | number | Date | Dayjs): Dayjs => {
  return dayjs.utc(utc).tz();
};

/**
 * @description utc 타임을 timezone에 맞는 format time으로 변환
 */
export const getFormattedTzTimeFromUtc = (
  utc: string | number | Date | Dayjs,
  format: TimeFormat = GLOBAL_TIME_LIVE_FORMAT,
) => {
  return getTzTimeFromUtc(utc).format(format);
};

/**
 * @description timezone time을 utc time으로 변환
 */
export const getUtcTimeFromTzTime = (tzTime: string | number | Date | Dayjs): Dayjs => {
  return dayjs.tz(tzTime).utc();
};

/**
 * @description timezone time을 utc format으로 변환
 */
export const getFormattedUtcTimeFromTzTime = (
  tzTime: string | number | Date | Dayjs = dayjs.tz(),
  format: TimeFormat = UTC_TIME_FORMAT,
) => {
  return getUtcTimeFromTzTime(tzTime).format(format);
};

/**
 * @description utc 타임 문자열을 utc format으로 변환
 */
export const getFormattedUtcTime = (
  utc: string | number | Date | Dayjs,
  format: TimeFormat = UTC_TIME_FORMAT,
): string => {
  return dayjs.utc(utc).format(format);
};

/**
 * @description 현재 utc time을 반환
 */
export const getUtcTimeNow = (): Dayjs => {
  return dayjs.utc(); /// ===  dayjs().utc();
};

/**
 * @description 현재 utc format time을 반환
 */
export const getFormattedUtcTimeNow = (format: string = UTC_TIME_FORMAT): string => {
  return getUtcTimeNow().format(format);
};

/**
 * @description 현재 timezone에 맞는 time을 반환
 */
export const getTzTimeNow = (): Dayjs => {
  return dayjs.tz();
};

/**
 * @description 현재 timezone에 맞는 format time을 반환
 */
export const getFormattedTzTimeNow = (format: string = GLOBAL_TIME_LIVE_FORMAT): string => {
  return getTzTimeNow().format(format);
};

/**
 * @description 두 날짜의 차이가 n일 이상인지 확인
 */
export const isDifferenceMoreThanNDays = (
  date1: dayjs.Dayjs,
  date2: dayjs.Dayjs,
  n: number,
): boolean => {
  const differenceInDays = date1.diff(date2, 'day');
  return Math.abs(differenceInDays) >= n;
};

export const convertRequestTime = () => getFormattedTzTimeNow(UTC_TIME_FORMAT);

const getLabel = (unit: 'SECOND' | 'MINUTE' | 'HOUR' | 'DAY', val: number) => {
  const suffix = val === 1 ? '' : 'S';
  return i18n.global.t(`WORD.${unit}${suffix}`, { value: val });
};

export const getCollectionIntervalWithUnit = (
  value: number,
  options?: {
    unit?: 's' | 'm';
    showZeroSeconds?: boolean;
    emptyValue?: string;
  },
): string => {
  const { unit = 's', showZeroSeconds = true, emptyValue = '-' } = options ?? {};
  if (value < 0) return emptyValue;

  const seconds = unit === 'm' ? value * ONE_MINUTE_IN_SEC : value;
  const days = Math.floor(seconds / ONE_DAY_IN_SECOND);
  const hours = Math.floor((seconds % ONE_DAY_IN_SECOND) / ONE_HOUR_IN_SEC);
  const minutes = Math.floor((seconds % ONE_HOUR_IN_SEC) / ONE_MINUTE_IN_SEC);
  const secs = seconds % ONE_MINUTE_IN_SEC;

  const parts: string[] = [];
  if (days > 0) parts.push(getLabel('DAY', days));
  if (hours > 0) parts.push(getLabel('HOUR', hours));
  if (minutes > 0) parts.push(getLabel('MINUTE', minutes));
  if (secs > 0 || (showZeroSeconds && parts.length === 0)) {
    parts.push(getLabel('SECOND', secs));
  }

  return parts.join(' ') || emptyValue;
};

/**
 * @description 주어진 toTime(타임존 기준)과 period 값을 이용해 fromTime을 계산한다.
 * @param toTime 기준 시간 (GLOBAL_TIME_RANGE_FORMAT)
 * @param period ex) 'p10m'
 * @param timeFormat 반환 및 파싱에 사용할 시간 포맷 (기본: GLOBAL_TIME_RANGE_FORMAT)
 * @returns fromTime 문자열 (timeFormat 기준)
 */
export const getFromTimeByPeriod = ({
  toTime,
  period,
  timeFormat = GLOBAL_TIME_RANGE_FORMAT,
}: {
  toTime: string;
  period: string;
  timeFormat?: TimeFormat;
}): string => {
  if (!dayjs(toTime, timeFormat).isValid()) {
    throw new Error('Invalid toTime format.');
  }
  const { offset } = parsePeriod(period);
  return dayjs(toTime, timeFormat).subtract(offset, 'second').format(timeFormat);
};

export const isValidTime = (
  time: unknown,
  format: TimeFormat = 'YYYY-MM-DD HH:mm:ss',
): time is string => typeof time === 'string' && dayjs(time, format, true).isValid();

export const getFormattedFractionalSeconds = (
  value: string,
  options?: { convertToTzTime?: boolean; convertToUtcTime?: boolean; fractionalDigits?: number },
) => {
  const { convertToTzTime = false, convertToUtcTime = false, fractionalDigits } = options ?? {};
  let fractionalPart = value.split('.')[1];

  let time = dayjs(value).format(UTC_TIME_FORMAT);
  if (convertToTzTime) {
    time = getTzTimeFromUtc(value).format(UTC_TIME_FORMAT);
  } else if (convertToUtcTime) {
    time = getUtcTimeFromTzTime(value).format(UTC_TIME_FORMAT);
  }
  if (fractionalDigits) {
    fractionalPart =
      fractionalPart?.length > fractionalDigits
        ? fractionalPart.slice(0, fractionalDigits)
        : padEnd(fractionalPart ?? '', fractionalDigits, '0');
  }

  return !isNil(fractionalPart) && fractionalPart !== '' ? `${time}.${fractionalPart}` : time;
};

export const isValidDateTime = (time: unknown): time is string => {
  return typeof time === 'string' && dayjs(time, 'YYYY-MM-DD', true).isValid();
};

export const addIntervalToTime = (time: number | string, interval: string) => {
  const datetime = dayjs(time);
  const intervalToSecond = getPeriodToSeconds(interval.slice(1));
  return datetime.add(intervalToSecond, 'second').format(UTC_TIME_FORMAT);
};

/**
 * time 값을 UTC 또는 timezone으로 변환한 뒤 offset(분)을 적용해 포맷된 문자열로 반환
 * convertToUtcTime가 true이면 UTC로 변환, false이면 timezone으로 변환
 * @param time 시간 문자열 utc 또는 timezone 타입
 * @param offset { value: number; unit: 's' | 'm' | 'h' | 'd' }
 * @param format 포맷
 * @param convertToUtcTime  true이면 UTC로 변환, false이면 timezone으로 변환
 * @returns 포맷된 문자열
 */
export const getFormattedTimeWithOffset = ({
  time,
  offset,
  format,
  convertToUtcTime = false,
}: {
  time: string | number | Date | Dayjs;
  offset: { value: number; unit: 's' | 'm' | 'h' | 'd' };
  format: TimeFormat;
  convertToUtcTime?: boolean;
}): string => {
  if (!time) return '';

  const convertedTime = convertToUtcTime ? getUtcTimeFromTzTime(time) : getTzTimeFromUtc(time);

  const { value, unit } = offset;

  return convertedTime.add(value, unit).format(format);
};
